import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import { useCreateItem, type Item } from '@/features/inventory/hooks';
import { useBomGraph, useCreateBom, type BomCategory } from '@/features/manufacturing/boms';
import { cn } from '@/lib/utils';

type Source = 'existing' | 'new';

interface NewBomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: BomCategory;
}

/**
 * Start a BOM. Finished goods always come from the catalog. A
 * sub-assembly can be an existing component (e.g. "Base") or a brand-new
 * assembly item (e.g. "Renew Base Assembly"), which is created under
 * Components with category "Sub-assembly". Either way the operator
 * lands in the builder to add components.
 */
export function NewBomDialog({ open, onOpenChange, category }: NewBomDialogProps) {
  const navigate = useNavigate();
  const { data: graph } = useBomGraph();
  const createBom = useCreateBom();
  const createItem = useCreateItem();

  const [source, setSource] = useState<Source>(category === 'sub_assembly' ? 'new' : 'existing');
  const [picked, setPicked] = useState<Item | null>(null);
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) {
      setSource(category === 'sub_assembly' ? 'new' : 'existing');
      setPicked(null);
      setSku('');
      setName('');
    }
  }, [open, category]);

  const filter = useMemo(
    () => (it: Item) => {
      if ((graph?.byItemId.get(it.id)?.lines.length ?? 0) > 0) return false;
      const kinds = [it.kind, ...(it.additional_kinds ?? [])];
      return category === 'finished_good'
        ? kinds.includes('finished_good')
        : kinds.some((k) => k !== 'finished_good');
    },
    [graph, category],
  );

  const submitting = createBom.isPending || createItem.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let item: Pick<Item, 'id' | 'name'>;
      if (source === 'existing') {
        if (!picked) {
          toast.error('Pick an item first.');
          return;
        }
        item = picked;
      } else {
        item = await createItem.mutateAsync({
          sku,
          name,
          kind: 'raw_material',
          category: 'Sub-assembly',
          status: 'in_development',
        });
      }
      await createBom.mutateAsync({ itemId: item.id, name: item.name });
      onOpenChange(false);
      navigate(`/mfg/boms/${item.id}?add=1`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the BOM.');
    }
  };

  const title = category === 'finished_good' ? 'New finished-good BOM' : 'New sub-assembly';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          {category === 'sub_assembly' && (
            <div className="inline-flex rounded-lg bg-secondary p-1 text-sm">
              {(
                [
                  ['new', 'Create new item'],
                  ['existing', 'Use existing item'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSource(value)}
                  className={cn(
                    'rounded-md px-3 py-1.5 font-medium transition-colors',
                    source === value
                      ? 'bg-card text-info shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {source === 'existing' ? (
            <ItemPicker
              label={category === 'finished_good' ? 'Finished good' : 'Assembly item'}
              value={picked?.id ?? null}
              onChange={setPicked}
              filter={filter}
              autoFocus
              placeholder={
                category === 'finished_good'
                  ? 'Search finished goods without a BOM…'
                  : 'Search components without a BOM…'
              }
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className="space-y-1.5">
                <Label htmlFor="sa-sku">SKU</Label>
                <Input
                  id="sa-sku"
                  value={sku}
                  onChange={(e) => setSku(e.target.value.toUpperCase())}
                  placeholder="SA-RNW-BASE"
                  className="font-mono"
                  autoFocus
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sa-name">Name</Label>
                <Input
                  id="sa-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Renew Base Assembly"
                  required
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                submitting || (source === 'existing' ? !picked : !sku.trim() || !name.trim())
              }
            >
              {submitting ? 'Creating…' : 'Create and open'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
